/**
 * The layouts somebody can put their CV into.
 *
 * A template here is data, not a component: a set of typographic decisions that the
 * preview, the PDF, the Word file and the LaTeX source all read. That is the only way
 * four renderers can agree on what "Compact" means, and it is why choosing one can
 * redraw the preview instantly without touching any of them.
 *
 * Every one of them is a single column. That is not a lack of imagination — a second
 * column is the single most expensive thing somebody can do to a CV, because the two
 * columns are interleaved on the way out and a parser reads a job title straight into
 * the middle of a date. The audit on the result page reports exactly that damage. It
 * would be incoherent to name the problem and then offer ten ways to reproduce it.
 *
 * What does vary is everything a person actually looks at: the face, the weight and
 * alignment of the name, how section headings are marked, how tightly it is set, the
 * accent, and whether there is room for a photograph.
 */

export type HeadingStyle = 'rule' | 'caps-rule' | 'plain' | 'accent' | 'filled';
export type Density = 'compact' | 'regular' | 'roomy';

/**
 * How an entry is built — the thing that actually makes one CV layout different
 * from another.
 *
 * The gallery used to be fourteen layouts that varied the serif, the rule under a
 * heading and the accent colour, and they were indistinguishable from each other at
 * any size, because none of those things is what a reader sees. What a reader sees
 * is which words are loud.
 *
 * Eye-tracking of recruiters triaging a stack puts the first pass at six to seven
 * seconds, spent on a short, fixed list: the name, the current title and employer,
 * the previous title and employer, the date ranges, and the education. Every one of
 * our layouts set the job title at the same weight as the company and the dates at
 * body size, so not one of them was built for the only pass most CVs get.
 *
 * These are arrangements of that list, not decorations of it:
 *
 * - `flat`      title and employer on one line, dates right. The conventional CV,
 *               and the right answer when the employer is the stronger name.
 * - `title-first` the job title on its own line and visibly larger, employer and
 *               dates beneath it. Loudest where the triage looks first.
 * - `rail`      titles left, every date in a fixed right-hand rail, so the career
 *               reads as a timeline down one edge.
 * - `masthead`  a large name over a full rule, then a tight title-first body. For
 *               a CV whose own name is the credential.
 */
export type Hierarchy = 'flat' | 'title-first' | 'rail' | 'masthead';

/**
 * How much bigger the job title is than body text, per arrangement.
 *
 * `flat` is 1 by definition — that is what makes it flat, and what every one of the
 * old layouts was.
 */
const TITLE_SCALE: Record<Hierarchy, number> = {
  flat: 1,
  'title-first': 1.3,
  rail: 1.12,
  masthead: 1.24,
};

export interface Template {
  id: string;
  name: string;
  /** One line: who this is for, in their terms. */
  blurb: string;
  /** Serif reads as traditional, sans as current. Both parse identically. */
  face: 'serif' | 'sans';
  nameAlign: 'left' | 'centre';
  headings: HeadingStyle;
  density: Density;
  /** How an entry is arranged. The choice that makes layouts tell apart. */
  hierarchy: Hierarchy;
  /** Hex, used for headings and rules. Absent means ink. */
  accent?: string;
  /** Reserves a square for a photograph beside the name. */
  photo?: boolean;
  /** Said plainly on the card, because this is the one choice with a wrong answer. */
  note?: string;
  /**
   * Behind the paywall.
   *
   * Not because it parses better — it does not, and saying so would be a lie we
   * could not back up. No independent study shows a template raising an ATS score,
   * and the one vendor test large enough to cite found single and double column
   * performing the same.
   *
   * Nor do they fit more on a page: measured against the free set, a paid layout
   * matches Compact and beats only the roomy ones, so "fits on one page" is not a
   * differentiator either. What they buy is typographic care — tracking, hairline
   * rules, a second face for dates — which is worth something to the person reading
   * it and nothing to the parser. That is the whole claim, and it is the one made
   * on the paywall.
   */
  premium?: boolean;
  /**
   * Colourways a paid layout comes in. The first is the layout as designed; each
   * other swaps only colours that were chosen to be swapped — the accent, and a
   * column's fill or band — and keeps the light-on-dark or dark-on-light pairing the
   * layout was set with, so no choice can make the text unreadable.
   */
  colours?: Colourway[];
  /** Letter-spacing on the name, in em. The cheapest mark of a considered setting. */
  nameTracking?: number;
  /** Letter-spacing on section headings, in em. */
  headingTracking?: number;
  /** A hairline reads as drawn; the default 0.6pt reads as a default. */
  ruleWeight?: number;
  /** Dates and locations in a second face, which separates them from the prose. */
  metaFace?: 'serif' | 'sans' | 'mono';
  /**
   * A coloured column down one side, the way most designed CVs are built.
   *
   * People ask for these by name — they are what a template site shows first — and
   * they are also the layout most likely to be misread by a screening system, which
   * can take a line from each column and glue them together. Two things keep ours
   * readable: the file writes the whole sidebar and then the whole main column, never
   * a line of one between lines of the other, so a parser reading in file order reads
   * each section intact; and every sidebar template carries a `note` saying plainly
   * that a single column is still the safer choice for an application portal.
   */
  sidebar?: Sidebar;
}

export interface Colourway {
  id: string;
  name: string;
  /** The swatch shown for it. */
  swatch: string;
  accent?: string;
  fill?: string;
  band?: string;
}

export interface Sidebar {
  side: 'left' | 'right';
  /** Width of the column, in points of an A4 page (595 wide). */
  width: number;
  /** The column's fill. */
  fill: string;
  /** Text on the fill: light on a dark column, ink on a pale one. */
  ink: 'light' | 'dark';
  /** A full-width band across the top carrying the name, in this colour. */
  band?: string;
  /**
   * What sits at the top of the column: their photograph in a circle, or — with no
   * photograph — their initials in one, which keeps the design without asking for a
   * picture that many employers would rather not see.
   */
  portrait: 'photo' | 'monogram';
}

/** Said on every two-column card, because it is the one thing about them that bites. */
export const SIDEBAR_NOTE =
  'Two columns read well to a person. This file keeps each column in one piece so it parses cleanly in our check, but some older screening systems still mix columns up — for an online application portal, a single column is the safer choice.';

export const TEMPLATES: Template[] = [
  {
    /**
     * The setting most technical CVs already use, and the one people arrive with.
     *
     * Deliberately `flat`: it is the shape of the widely-copied LaTeX CV, so the
     * rebuild looks like the document they uploaded rather than like something
     * else — which is the difference between trusting the export and not. Default
     * for that reason, even though it is not the one built for the seven seconds.
     */
    id: 'standard',
    name: 'Standard',
    blurb: 'Centred name, small-capital sections, tight. What most CVs already look like.',
    face: 'sans',
    nameAlign: 'centre',
    headings: 'caps-rule',
    density: 'compact',
    hierarchy: 'flat',
    headingTracking: 0.08,
    ruleWeight: 0.5,
  },
  {
    /**
     * The one built around what a recruiter actually does with a CV.
     *
     * Eye-tracking of a triage pass puts it at six to seven seconds on the name,
     * the titles, the employers and the dates. This sets the job title a third
     * larger than the prose and on its own line, with the employer and the dates
     * beneath it — so the fields that decide the pass are the fields you see.
     */
    id: 'scan',
    name: 'Scan',
    blurb: 'Job title set large on its own line. Built for the seven seconds a recruiter gives it.',
    face: 'sans',
    nameAlign: 'left',
    headings: 'rule',
    density: 'regular',
    hierarchy: 'title-first',
    headingTracking: 0.06,
    ruleWeight: 0.5,
  },
  {
    /**
     * Dates pulled out of the prose into one column, so the shape of a career is
     * readable without reading any of it — gaps, overlaps and tenure all land on
     * one edge instead of being buried at the end of a line.
     */
    id: 'timeline',
    name: 'Timeline',
    blurb: 'Every date in one right-hand column, so your career reads as a timeline.',
    face: 'sans',
    nameAlign: 'left',
    headings: 'caps-rule',
    density: 'compact',
    hierarchy: 'rail',
    headingTracking: 0.08,
    ruleWeight: 0.5,
  },
  {
    id: 'classic',
    name: 'Classic',
    blurb: 'The safe default. Serif name, ruled sections, room to breathe.',
    face: 'serif',
    nameAlign: 'left',
    headings: 'rule',
    density: 'regular',
    hierarchy: 'flat',
  },
  {
    id: 'academic',
    name: 'Academic',
    blurb: 'Serif, roomy, plain headings. Suits publications and long lists.',
    face: 'serif',
    nameAlign: 'centre',
    headings: 'plain',
    density: 'roomy',
    hierarchy: 'flat',
  },
  {
    id: 'photo',
    name: 'With photo',
    blurb: 'Keeps a square for a photograph beside your name.',
    note: 'Many employers ask for no photograph. Check before you send it.',
    face: 'sans',
    nameAlign: 'left',
    headings: 'rule',
    density: 'regular',
    hierarchy: 'flat',
    photo: true,
  },
  {
    /**
     * A pale tinted column with a round portrait — the soft look of the most-picked
     * designed CVs. Ink on blush, so it still prints on any office printer.
     */
    id: 'blush',
    name: 'Blush',
    blurb: 'A soft tinted column with your photo or initials. Friendly, and still easy to scan.',
    note: SIDEBAR_NOTE,
    face: 'serif',
    nameAlign: 'left',
    headings: 'caps-rule',
    density: 'compact',
    hierarchy: 'title-first',
    accent: '#9A5B4F',
    headingTracking: 0.08,
    sidebar: { side: 'left', width: 186, fill: '#F4E9E4', ink: 'dark', portrait: 'photo' },
  },
  {
    /**
     * The column on the right, with initials instead of a photo. The work comes first
     * in reading order and on the page, which is the better order for a recruiter.
     */
    id: 'ledger',
    name: 'Ledger',
    blurb: 'Your work on the left, skills and education in a quiet column on the right. Initials, no photo.',
    note: SIDEBAR_NOTE,
    face: 'sans',
    nameAlign: 'left',
    headings: 'caps-rule',
    density: 'compact',
    hierarchy: 'flat',
    headingTracking: 0.08,
    ruleWeight: 0.5,
    sidebar: { side: 'right', width: 176, fill: '#F1F3F6', ink: 'dark', portrait: 'monogram' },
  },
];

const PREMIUM_TEMPLATES: Template[] = [
  {
    /**
     * The one arrangement that is not in the free set.
     *
     * A name at twenty-five point over a full rule, then a tight title-first body
     * under it. It only works when the person's own name is part of the credential,
     * which is why it is not the default for anyone.
     */
    id: 'masthead',
    name: 'Masthead',
    blurb: 'A large name over a full rule, then a tight hierarchy. Reads like a letterhead.',
    face: 'serif',
    nameAlign: 'left',
    headings: 'plain',
    density: 'compact',
    hierarchy: 'masthead',
    premium: true,
    colours: [{ id: 'ink', name: 'Ink', swatch: '#111111' }, { id: 'navy', name: 'Navy', swatch: '#1F3A5F', accent: '#1F3A5F' }, { id: 'maroon', name: 'Maroon', swatch: '#7A2E3A', accent: '#7A2E3A' }, { id: 'forest', name: 'Forest', swatch: '#1F5E46', accent: '#1F5E46' }],
    nameTracking: -0.02,
    metaFace: 'serif',
  },
  {
    id: 'signal',
    name: 'Signal',
    blurb: 'Timeline rail with the dates in monospace, so every entry lines up exactly.',
    face: 'sans',
    nameAlign: 'left',
    headings: 'accent',
    density: 'compact',
    hierarchy: 'rail',
    accent: '#0F766E',
    premium: true,
    colours: [{ id: 'teal', name: 'Teal', swatch: '#0F766E' }, { id: 'navy', name: 'Navy', swatch: '#1E3A8A', accent: '#1E3A8A' }, { id: 'plum', name: 'Plum', swatch: '#6B2E5F', accent: '#6B2E5F' }, { id: 'rust', name: 'Rust', swatch: '#9A3412', accent: '#9A3412' }],
    metaFace: 'mono',
    headingTracking: 0.07,
  },
  {
    id: 'slate',
    name: 'Slate',
    blurb: 'Filled heading bars in deep ink, title-dominant. For dense technical CVs.',
    face: 'sans',
    nameAlign: 'left',
    headings: 'filled',
    density: 'compact',
    hierarchy: 'title-first',
    accent: '#0F172A',
    premium: true,
    colours: [{ id: 'ink', name: 'Ink', swatch: '#0F172A' }, { id: 'navy', name: 'Navy', swatch: '#1E3A5F', accent: '#1E3A5F' }, { id: 'burgundy', name: 'Burgundy', swatch: '#6B1F2A', accent: '#6B1F2A' }, { id: 'forest', name: 'Forest', swatch: '#1F4D3A', accent: '#1F4D3A' }],
    metaFace: 'mono',
  },
  {
    /** Deep navy column, white type, round portrait. The classic designed CV. */
    id: 'harbour',
    name: 'Harbour',
    blurb: 'A deep navy column with your photo, white type, and a large name beside it.',
    note: SIDEBAR_NOTE,
    face: 'sans',
    nameAlign: 'left',
    headings: 'caps-rule',
    density: 'compact',
    hierarchy: 'title-first',
    accent: '#1F2F4D',
    premium: true,
    colours: [{ id: 'navy', name: 'Navy', swatch: '#1F2F4D' }, { id: 'forest', name: 'Forest', swatch: '#1E3D33', accent: '#1E3D33', fill: '#1E3D33' }, { id: 'burgundy', name: 'Burgundy', swatch: '#4A1E2A', accent: '#4A1E2A', fill: '#4A1E2A' }, { id: 'graphite', name: 'Graphite', swatch: '#2B2D33', accent: '#2B2D33', fill: '#2B2D33' }],
    nameTracking: 0.02,
    headingTracking: 0.1,
    metaFace: 'sans',
    sidebar: { side: 'left', width: 190, fill: '#1F2F4D', ink: 'light', portrait: 'photo' },
  },
  {
    /** A full-width band carrying the name and photo, then two columns under it. */
    id: 'banner',
    name: 'Banner',
    blurb: 'Your name and photo across a coloured band, with two columns beneath.',
    note: SIDEBAR_NOTE,
    face: 'serif',
    nameAlign: 'left',
    headings: 'caps-rule',
    density: 'compact',
    hierarchy: 'title-first',
    accent: '#2F4A6D',
    premium: true,
    colours: [{ id: 'steel', name: 'Steel', swatch: '#2F4A6D' }, { id: 'teal', name: 'Teal', swatch: '#0F5F5A', accent: '#0F5F5A', band: '#0F5F5A', fill: '#EAF4F2' }, { id: 'wine', name: 'Wine', swatch: '#6D2E46', accent: '#6D2E46', band: '#6D2E46', fill: '#F6ECEF' }, { id: 'olive', name: 'Olive', swatch: '#4A5A2A', accent: '#4A5A2A', band: '#4A5A2A', fill: '#F1F3EA' }],
    headingTracking: 0.08,
    metaFace: 'sans',
    sidebar: { side: 'left', width: 180, fill: '#EEF2F7', ink: 'dark', band: '#2F4A6D', portrait: 'photo' },
  },
  {
    /** Charcoal column with a teal accent on the headings. For design-led roles. */
    id: 'charcoal',
    name: 'Charcoal',
    blurb: 'A charcoal column and teal headings. Bold, for design and product roles.',
    note: SIDEBAR_NOTE,
    face: 'sans',
    nameAlign: 'left',
    headings: 'accent',
    density: 'compact',
    hierarchy: 'title-first',
    accent: '#0F766E',
    premium: true,
    colours: [{ id: 'teal', name: 'Teal', swatch: '#0F766E' }, { id: 'indigo', name: 'Indigo', swatch: '#3730A3', accent: '#3730A3' }, { id: 'rust', name: 'Rust', swatch: '#9A3412', accent: '#9A3412' }, { id: 'plum', name: 'Plum', swatch: '#6B2E5F', accent: '#6B2E5F' }],
    headingTracking: 0.06,
    metaFace: 'mono',
    sidebar: { side: 'left', width: 186, fill: '#33363B', ink: 'light', portrait: 'monogram' },
  },
];

export const ALL_TEMPLATES: Template[] = [...TEMPLATES, ...PREMIUM_TEMPLATES];

export const DEFAULT_TEMPLATE = TEMPLATES[0]!;

export const templateById = (id: string): Template =>
  ALL_TEMPLATES.find((t) => t.id === id) ?? DEFAULT_TEMPLATE;

/**
 * The one set of numbers every renderer scales from.
 *
 * Point sizes, because the PDF is laid out in points and the preview can divide down
 * to pixels. Keeping one table means the preview cannot drift from the file it is
 * previewing, which is the whole promise of showing it.
 */
export interface Metrics {
  name: number;
  contact: number;
  heading: number;
  subheading: number;
  meta: number;
  body: number;
  leading: number;
  gapBefore: { heading: number; subheading: number; text: number };
  gapAfter: { name: number; heading: number; subheading: number; bullet: number; text: number };
  margin: number;
}

const DENSITY: Record<Density, Pick<Metrics, 'leading' | 'margin'> & { scale: number; gap: number }> = {
  compact: { leading: 1.1, margin: 32, scale: 0.94, gap: 0.78 },
  regular: { leading: 1.14, margin: 36, scale: 1, gap: 1 },
  roomy: { leading: 1.22, margin: 44, scale: 1.04, gap: 1.35 },
};

/**
 * The steps a document is allowed to grow by to fill the page it is on, largest first.
 *
 * A CV that ends a third of the way up the page looks like it ran out of things to
 * say. Their own CV filled its page; ours left the bottom empty because the setting
 * was tightened to pull a two-page render back to one, and then stayed tight when it
 * no longer needed to be. Growing is only ever tried while the document still fits on
 * one page, and both renderers try the same steps in the same order so the preview
 * and the file land on the same one.
 */
export const FIT_STEPS = [1.18, 1.14, 1.1, 1.06, 1.03, 1];

/**
 * Every setting a document may be drawn at, largest first: grow to fill a short page,
 * then shrink a little to keep a long one on one page.
 *
 * One list for the preview and the file. They used to differ — the file would shrink
 * a long CV to 92% to keep it on one page while the preview never went below 100% —
 * so a CV could show two pages on screen and download as one. Both now take the
 * first step that fits on a page, and if none does, draw at full size and run on.
 *
 * Paid layouts may shrink a little further before giving up on one page: 82% is still
 * comfortably readable at A4, and that is a measurable difference, not a claim.
 */
export function ladderFor(template: Template): number[] {
  return [...FIT_STEPS, ...(template.premium ? [0.96, 0.92, 0.88, 0.85, 0.82] : [0.96, 0.92, 0.88])];
}

export function metricsFor(template: Template, fit = 1): Metrics {
  const { leading, margin, scale: density, gap } = DENSITY[template.density];
  const scale = density * fit;
  const s = (n: number) => Math.round(n * scale * 100) / 100;
  const g = (n: number) => Math.round(n * gap * fit * 100) / 100;

  const hierarchy = template.hierarchy;
  /**
   * The job title, sized by the arrangement rather than by the density.
   *
   * Body stays where it is. Making the triage fields louder means raising the title
   * against the prose, not raising everything — a layout where all of it grew would
   * just be the roomy density again.
   */
  const title = 9.5 * TITLE_SCALE[hierarchy];

  return {
    name: s(hierarchy === 'masthead' ? 25 : template.nameAlign === 'centre' ? 19 : 17),
    contact: s(8.5),
    heading: s(10.5),
    subheading: s(title),
    meta: s(8.5),
    body: s(9.5),
    leading,
    margin,
    gapBefore: { heading: g(9), subheading: g(5), text: g(1) },
    gapAfter: { name: g(2), heading: g(3), subheading: g(0.5), bullet: g(1.5), text: g(2) },
  };
}

/**
 * The layout in one of its colourways. Unknown or absent ids give the layout as
 * designed, so an old link or a free layout is never broken by the choice.
 */
export function withColour(template: Template, colourId: string | null | undefined): Template {
  const way = template.colours?.find((c) => c.id === colourId);
  if (!way || way === template.colours?.[0]) return template;
  return {
    ...template,
    ...(way.accent ? { accent: way.accent } : {}),
    ...(template.sidebar
      ? { sidebar: { ...template.sidebar, ...(way.fill ? { fill: way.fill } : {}), ...(way.band ? { band: way.band } : {}) } }
      : {}),
  };
}
